/**
 * Safe public corporate profile data — bilingual content for /:lang/corporate-profile.
 *
 * Source of truth: official Articles of Incorporation + NoJo Enterprise
 * Company Documents (履歴事項全部証明書) dated 2026-05-14.
 *
 * NEVER add: scanned legal documents, private addresses, signatures, seals,
 * shareholder details, or raw registration images.
 *
 * 2026-02-02 fix pass — owner-confirmed values:
 *   • Capital: ¥50,000,000 (5,000万円) — owner-confirmed display value
 *   • Established: 2013-12-03
 *   • Corporate Number (法人番号): 5021001052313
 *     https://www.houjin-bangou.nta.go.jp/henkorireki-johoto.html?selHouzinNo=5021001052313
 *   • Invoice Registration Number (適格請求書): T5021001052313
 *     https://www.invoice-kohyo.nta.go.jp/regno-search/detail?selRegNo=5021001052313
 *   • Primary Bank: Bank of Yokohama (横浜銀行)
 *   • Accounting / Tax firm reference: TokyoTax 東京税務会計事務所
 *     http://www.tokyotax.co.jp/
 */
export const corporateProfile = {
  legalNameJa: "株式会社ＮｏＪｏ　Ｅｎｔｅｒｐｒｉｓｅ",
  legalNameEn: "NoJo Enterprise Co., Ltd.",
  established: "2013-12-03",
  establishedDisplayJa: "2013年12月3日",
  establishedDisplayEn: "December 3, 2013",
  capitalJpy: 50000000,
  capitalDisplayJa: "5,000万円",
  capitalDisplayEn: "¥50,000,000",
  locationJa: "神奈川県横須賀市",
  locationEn: "Yokosuka, Kanagawa, Japan",
  primaryServices: [
    "JapanETCcard.com",
    "JapanTollReceipts.jp",
    "JapanChangeMoney.com",
  ],
  primaryBank: "Bank of Yokohama (横浜銀行)",
  accountingFirmName: "東京税務会計事務所 (TokyoTax)",
  accountingFirmUrl: "http://www.tokyotax.co.jp/",
  corporateNumber: "5021001052313",
  invoiceRegistrationNumber: "T5021001052313",
  invoiceRegistrationDateJa: "令和5年10月1日",
  invoiceRegistrationDateEn: "October 1, 2023",
};

/** Official corporate profile table rows (label → value). */
export const profileRows = (lang) => {
  const isJa = lang === "ja";
  return [
    {
      key: "companyName",
      label: isJa ? "社名" : "Company Name",
      value: isJa ? "株式会社ＮｏＪｏ　Ｅｎｔｅｒｐｒｉｓｅ" : "NoJo Enterprise Co., Ltd.",
    },
    {
      key: "legalNameAlt",
      label: isJa ? "英文社名" : "Registered Japanese Legal Name",
      value: isJa ? "NoJo Enterprise Co., Ltd." : "株式会社ＮｏＪｏ　Ｅｎｔｅｒｐｒｉｓｅ",
    },
    {
      key: "representative",
      label: isJa ? "代表取締役" : "Representative Director",
      value: isJa ? "ジョーンズ・アダム" : "Adam Jones",
    },
    {
      key: "established",
      label: isJa ? "設立" : "Established",
      value: isJa ? "2013年12月3日" : "December 3, 2013",
    },
    {
      key: "address",
      label: isJa ? "所在地" : "Registered Address",
      value: isJa
        ? "〒238-0041 神奈川県横須賀市本町2-2-1 高村ビル202号室"
        : "#202 Takamura Building, 2-2-1 Honcho, Yokosuka 238-0041, Japan",
    },
    {
      key: "capital",
      label: isJa ? "資本金" : "Capital",
      value: isJa ? "5,000万円" : "¥50,000,000",
    },
    {
      key: "corporateNumber",
      label: isJa ? "法人番号" : "Corporate Number",
      value: "5021001052313",
      link: "https://www.houjin-bangou.nta.go.jp/henkorireki-johoto.html?selHouzinNo=5021001052313",
    },
    {
      key: "invoiceRegistration",
      label: isJa ? "適格請求書発行事業者登録番号" : "Invoice Registration Number",
      value: "T5021001052313",
      link: "https://www.invoice-kohyo.nta.go.jp/regno-search/detail?selRegNo=5021001052313",
      meta: isJa
        ? "登録年月日:令和5年10月1日 (2023年10月1日)"
        : "Registered October 1, 2023 (令和5年10月1日)",
    },
    {
      key: "businessAreas",
      label: isJa ? "主な事業領域" : "Main Business Areas",
      value: isJa
        ? "ソフトウェア・ウェブシステムの開発、ETCカード関連サービス、ETC機器の取扱い、ETC利用記録・レポート、多言語カスタマーサポート、両替業・外国為替取引業、ウェブサイト運営、デジタル業務支援"
        : "Software & web-system development, ETC-card related services, ETC device handling, ETC toll-record reporting, multilingual customer support, money-exchange business, foreign exchange business, website operations, digital business support",
    },
    {
      key: "operatedServices",
      label: isJa ? "運営サービス" : "Operated Services",
      services: ["JapanETCcard.com", "JapanTollReceipts.jp", "JapanChangeMoney.com"],
    },
    {
      key: "primaryBank",
      label: isJa ? "主要取引銀行" : "Primary Bank",
      value: isJa ? "株式会社横浜銀行" : "Bank of Yokohama, Ltd.",
    },
    {
      key: "accountingFirm",
      label: isJa ? "顧問税理士" : "Accounting Firm",
      value: isJa ? "東京税務会計事務所" : "TokyoTax (東京税務会計事務所)",
      link: "http://www.tokyotax.co.jp/",
    },
  ];
};

/**
 * Full registered scope of business — exact list from the official
 * Articles of Incorporation. Used in a dedicated "Scope of business
 * (as registered)" section so banks, vendors, and partners can read
 * the complete legal scope at a glance. This is the formal scope on
 * file at the 横浜地方法務局横須賀支局 — not a marketing claim of
 * actively operated services.
 */
export const registeredBusinessScope = (lang) => {
  const isJa = lang === "ja";
  if (isJa) {
    return [
      "ソフトウェア及びウェブシステムの開発、制作及び販売",
      "ウェブを通じて企業がする国際的コミュニケーションツールの提供",
      "ウェブサイトの運営",
      "ウェブサイトを通じた商品の販売、広告宣伝、海外居住者への情報提供・交換",
      "ETCカード取扱代行業",
      "ETCカード管理システムを活用した決済代行業務",
      "ETC機器の販売、仕入、輸出入及び取付け、並びに保守、管理",
      "米兵及びその他外国人向けサービス業の経営",
      "米兵及びその他外国人に対する国内居住におけるコンサルタント業務",
      "公共料金等の支払い代行業務",
      "両替業",
      "外国為替取引業",
      "貸金業",
      "車両の売買、輸出入及びリース業務",
      "古物商",
      "不動産の売買、賃貸、管理及びその仲介",
      "飲食店の経営",
      "保険代理店及び保険仲立業",
      "暗号資産・デジタル資産の保有、運用、管理及び自社商品・サービス対価としての受領",
      "有価証券、暗号資産その他資産の保有、運用及び管理",
      "前各号に付帯関連する一切の業務",
    ];
  }
  return [
    "Development, production, and sales of software and web systems",
    "Provision of international communication tools for businesses via the web",
    "Operation of websites",
    "Sales of products, advertising, and information exchange with overseas residents via websites",
    "ETC card handling agency",
    "Payment-handling agency services utilizing ETC card management systems",
    "Sales, procurement, import/export, installation, maintenance and management of ETC devices",
    "Operation of service businesses for U.S. military personnel and other foreign nationals",
    "Consulting services for U.S. military personnel and other foreign nationals on residency in Japan",
    "Agency services for utility and various other public-fee payments",
    "Money exchange business",
    "Foreign exchange business",
    "Lending business",
    "Sales, import/export, and leasing of vehicles",
    "Secondhand-goods dealer (antiques)",
    "Sales, rental, management, and brokerage of real estate",
    "Operation of restaurants and food businesses",
    "Insurance agency and insurance brokerage business",
    "Holding, operation, management of crypto-assets and digital assets, and receipt of such assets as consideration for the company's own products and services",
    "Holding, operation, and management of securities, crypto-assets, and other assets",
    "Any other business incidental or related to the preceding items",
  ];
};

/** Polished public business-area categories (NOT the raw legal-purpose list). */
export const businessAreas = (lang) => {
  const isJa = lang === "ja";
  return [
    { key: "software", label: isJa ? "ソフトウェア・ウェブシステム開発" : "Software & web-system development", accent: "blue" },
    { key: "etcCard", label: isJa ? "ETCカード関連サービス" : "ETC-card related services", accent: "purple" },
    { key: "tollRecord", label: isJa ? "ETC利用記録・レポートサービス" : "ETC toll-record & reporting services", accent: "green" },
    { key: "multilingual", label: isJa ? "多言語カスタマーサポート" : "Multilingual customer support", accent: "navy" },
    { key: "exchange", label: isJa ? "両替業・外国為替取引業（準備中）" : "Money-exchange & foreign-exchange services (planned)", accent: "blue" },
    { key: "digital", label: isJa ? "デジタル業務支援" : "Digital business support", accent: "navy" },
    { key: "vehicle", label: isJa ? "車両関連サービス" : "Vehicle-related services", accent: "purple" },
    { key: "consulting", label: isJa ? "コンサルティング・運営支援" : "Consulting & operational support", accent: "green" },
  ];
};

/** Operated services (mirror SERVICE_BRANDS but with profile-page wording). */
export const operatedServices = (lang) => {
  const isJa = lang === "ja";
  return [
    {
      slug: "japanetccard",
      domain: "JapanETCcard.com",
      accent: "purple",
      accentHex: "#5B2E83",
      tint: "#F5F0FA",
      category: isJa ? "ETCカード支援 / モビリティ" : "ETC card support & mobility services",
      body: isJa
        ? "日本でETCカード関連サービスを必要とする外国人のお客様、企業利用者、米軍・政府関係者などを実務面から支援する独立サービス。"
        : "An independent service supporting foreign residents, corporate users and military-connected customers who need practical help with ETC-card related procedures in Japan.",
    },
    {
      slug: "japantollreceipts",
      domain: "JapanTollReceipts.jp",
      accent: "green",
      accentHex: "#2F8F3C",
      tint: "#F3FAEE",
      category: isJa ? "ETC利用記録・レポート" : "ETC toll-record automation & reporting",
      body: isJa
        ? "ETC利用記録を整理し、個人・企業・管理者が確認しやすいレポート作成を支援する独立サービス。"
        : "An independent service that organizes ETC usage records and produces clearer toll reports for individuals, companies and managers.",
    },
    {
      slug: "japanchangemoney",
      domain: "JapanChangeMoney.com",
      accent: "blue",
      accentHex: "#0E5FB0",
      tint: "#EFF6FF",
      category: isJa ? "外貨両替・両替関連 (準備中)" : "Money exchange & related access (planned)",
      body: isJa
        ? "透明性のある外貨両替体験を目指す準備中のサービス。提供開始時期およびライセンス状況は別途ご案内します。"
        : "A planned money-exchange service focused on transparent rates, customer education and compliance-aware workflows. Launch date and licensing status will be announced separately.",
    },
  ];
};
